import { z } from "zod";
import { EVENT_KIND_OPTIONS } from "@libs/utils";
import { eventStartLocalSchema } from "../event-time";
import { collectOrNullSchema } from "@libs/platform-wire";
import { getAccessOutputSchema } from "../schema";

/**
 * Wire-shape schemas for access-related queryFns durably cached in
 * `apps/mobile/utils/durableCache`.
 *
 * `accessTicketSchema` matches a single access entity (the discriminated
 * union of `event`, `music` and `merch` variants from `functions/api/schema.ts`).
 * `accessTicketsListSchema` is the array shape returned by both
 * `accesses.listAll` (full feed) and `user.accesses.listUserAccesses`
 * (the requesting user's purchased tickets).
 *
 * Co-located with the existing schema rather than duplicated here — the
 * single source of truth is `getAccessOutputSchema` at
 * `functions/api/schema.ts`. This file re-exports it under names that
 * match the consumer call site for grep-ability and scope clarity.
 */
export const accessTicketSchema = getAccessOutputSchema;
export type AccessTicket = z.infer<typeof accessTicketSchema>;

export const accessTicketsListSchema = z.array(accessTicketSchema);

/**
 * Purpose-built wire shape for the player access carousel.
 *
 * The storefront endpoint returns complete access entities because its callers
 * render detail-rich lists. The player carousel needs only enough data to draw
 * one locked-music card and one upcoming-event card. In particular, this shape
 * deliberately cannot carry a playable audio URL.
 */
export const artistAccessCarouselSchema = z.object({
  music: z
    .object({
      accessId: z.string(),
      songId: z.string(),
      title: z.string(),
      subtitle: z.string(),
      artworkUrl: z.string().nullable(),
      hasPreview: z.boolean(),
    })
    .nullable(),
  event: z
    .object({
      accessId: z.string(),
      title: z.string(),
      city: z.string(),
      kind: z.enum(EVENT_KIND_OPTIONS).nullable().optional(),
      startsAtLocal: eventStartLocalSchema,
    })
    .nullable(),
});

export type ArtistAccessCarousel = z.infer<typeof artistAccessCarouselSchema>;
export type AccessTicketsList = z.infer<typeof accessTicketsListSchema>;

/**
 * Wire-shape schema for `accesses.viewTicket`. Returns the access plus the
 * requesting user's purchased tickets. Used by the client durable-cache
 * layer so concert/event tickets render at the venue with no signal.
 * `collect` is My Tickets as the server compiles it for this viewer
 * (`collectFor`); null when the tickets are not a seller purchase.
 */
export const viewTicketWireSchema = z.object({
  access: getAccessOutputSchema,
  collect: collectOrNullSchema,
});
export type ViewTicket = z.infer<typeof viewTicketWireSchema>;

/**
 * Wire-shape schema for `user.accesses.listUnlockedPrivateSongs` — the array
 * of song ids the requesting user has unlocked via private/access purchases.
 * Mirrors the inline `.output(...)` schema in the trpc router. Used by the
 * client durable-cache layer to validate cached rows on read so cold-start
 * paywall gating resolves from SQLite.
 */
export const unlockedPrivateSongsWireSchema = z.array(z.string());
export type UnlockedPrivateSongs = z.infer<
  typeof unlockedPrivateSongsWireSchema
>;
